import { LitElement, html, css, TemplateResult } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { VoyageCheckbox } from '../../voyage-checkbox/src/voyage-checkbox';
import { debounce, isOnline } from '../../../src/utils/helpers';
import { LegSpeedSettings, Signature, VesselCharacteristics, VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { CalculatedValues, DataExtractor, WPAndMetadata, wpMetadata, WPSections } from './data-extractor';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { until } from 'lit/directives/until';

const MpsToKts = 60*60 / 1852;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement('voyage-passage-plan-voyage')
export class VoyagePassagePlanVoyage extends LitElement
{
    @property({ type: Boolean, attribute: false }) readOnly: boolean = false;
    @property({ type: Boolean }) isOfflinePlan: boolean = false;
    @property({ type: Boolean }) showPassagePlanFromPlan: boolean = false;


    constructor()
    {
        super();
        
        this._calcUkc = this._calcUkc.bind(this);
        
        VesselPlanningModel!.getVesselCharacteristics().then( vc => this._vesselCharacteristics = vc );
        this._savePassagePlan = this._savePassagePlan.bind(this);
    }

    connectedCallback(): void {
        super.connectedCallback();
        VesselPlanningModel!.addEventListener('save-passage-plan', this._savePassagePlan);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        VesselPlanningModel!.removeEventListener('save-passage-plan', this._savePassagePlan);
    }

    private _savePassagePlan() {
        //TODO: check if this is needed
        if (this.isOfflinePlan) {
            return;
        }
        VesselPlanningModel!.setPlan(this._plan!.scheduleId, this._plan!, false);
    }

    private _calcUkc() {
            DataExtractor.calcUKCs(this._plan!, DataExtractor.splitRoute(this._plan!)!);
            this.requestUpdate();
    }

    protected createRenderRoot() {
        const root = super.createRenderRoot();
        root.addEventListener('input', <EventListener>((evt:InputEvent) => this._onInput(evt)));
        return root;
      }

    @property({ type: Object, attribute: false })
    get plan(): VoyagePlan|null
    {
        return this._plan;
    }

    set plan(plan)
    {
        const old = this._plan;
        this._plan = plan;
        if (!old && this._plan) {
            // If we have a new plan, make sure we calculate UKC in initial state
            this._calcUkc();
        }
        
        if (this._savingPlanCounter > 0)
        {
            this._savingPlanCounter--;
            // If we have a new plan because we saved changes, make sure any changes done after the save
            // is kept (so we don't accidentally throw away changes done between the save and now)
            this._plan = this._mergeSavedPlan(old!, plan!);
        }
        this.requestUpdate('plan', old);
    }

    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            .container {
                display: flex;
                flex-direction: column;
                margin: 20px;
                color: var(--neutrals-grey-02, black);
                font-family: MaerskText;
                --pp-voyage-cell-height: 51px;
            }

            h2, .waypointsHeaderRight, .waypointsDistance {
                font-weight: bold;
                font-size: 16px;
                color: var(--neutrals-grey-02, black);
                margin-top: 40px;
                margin-bottom: 20px;
            }

            .tableTitle {
                margin-top: 50px;
                margin-bottom: 20px;
            }

            .waypointsHeaderRight {
                font-weight: normal;
                text-align: right;
                margin-top: 20px;
                margin-bottom: 20px;
            }

            .waypointsHeader {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
            }

            .waypointsHeader>h2 {
                margin-top: 20px;
                margin-bottom: 20px;
            }

            label {
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                margin-bottom: 5px;
            }

            textarea {
                text-align: left;
                box-sizing: border-box;
                color: var(--neutrals-grey-02, black);
                background-color: unset;
                font-size: 14px;
                font-family: MaerskText;
                border: none;
                resize: none;
            }

            textarea:focus-within {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
                outline: none;
                z-index: 1;
            }

            .warning {
                padding: 20px;
                align-self: center;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                border: 1px solid var(--warning-yellow-8, yellow);
                border-radius: 3px;
                background-color: rgba(var(--warning-yellow-10-rgb, yellow), 0.63);
            }

            .flexRow {
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                justify-content: space-between;
                margin-top: 50px;
            }

            .noMargin {
                margin: 0;
            }

            .table {
                display: grid;
                grid-auto-flow: row;
                gap: 1px;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                width: 540px;
            }

            .waypoints {
                grid-auto-flow: row dense;
                grid-template-columns: repeat(20, auto);
                grid-auto-rows: var(--pp-voyage-cell-height);
                width: auto; //width: 1230px;
            }

            .nocatzoc {
                grid-template-columns: repeat(19, auto);
            }

            .doubleColumn {
                grid-column: span 2;
            }

            .doubleRow {
                grid-row: span 2;
            }

            .passagePlan {
                grid-column: span 7;
            }
            .nocatzoc .passagePlan {
                grid-column: span 6;
            }

            .waypointsDistance {
                text-align: center;
                margin-top: 25px;
                margin-bottom: 25px;
            }

            #positionValidation {
                height: 50px;
                width: calc(50% - 20px);
            }

            .cell {
                display: flex;
                align-items: center;
                outline: 1px solid var(--neutrals-grey-07, grey);
                padding-right: 20px;
                padding-left: 20px;
            }

            .waypoints>.cell {
                padding-right: 5px;
                padding-left: 5px;
                justify-content: center;
                white-space: normal;
                text-align: justify;
                -moz-text-align-last: center; /* Firefox 12+ */
                text-align-last: center;
            }

            .legend {
                background-color: var(--neutrals-grey-09, grey);
                justify-content: flex-end;
            }

            .legendTop {
                background-color: var(--neutrals-grey-09, grey);
                justify-content: center;
            }

            .lop {
                min-width: 98px;
            }

            .waypoints>.wpDetails {
                white-space: nowrap;
                padding-right: 20px;
                padding-left: 20px;
            }

            .waypoints>.noPadding {
                padding: 0;
            }

            .openWaters {
                font-weight: bold;
                margin-right: 5px;
            }

            .waypointsNotes {
                font-size: 14px;
            }

            .dropdown {
                width: 40px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                background-color: var(--neutrals-grey-10, white);
            }

            .dropdown.signature {
                width: 100%;
            }

            .distances {
                grid-template-columns: 1fr 1fr 1fr 1fr;
                grid-template-rows: repeat(5, var(--pp-voyage-cell-height));
                margin-right: 40px;
            }

            .draughts {
                grid-template-columns:  1fr 1fr 1fr 1fr 1fr;
                grid-template-rows: repeat(5, var(--pp-voyage-cell-height));
                width: auto;
            }

            .draughtsNote {
                grid-column: span 3;
            }

            .inlineInput {
                display: inline-block;
                text-align: right;
                width: 50px;
                border: none;
                color: var(--neutrals-grey-02, black);
                background-color: unset;
                font-size: 14px;
                font-family: MaerskText;
            }

            .inlineInput.signature {
                width: 100%;
            }

            .squats {
                grid-template-columns: 1fr 1fr 1fr 1fr;
                grid-template-rows: repeat(5, var(--pp-voyage-cell-height));
                margin-right: 40px;
            }

            .underKeel {
                grid-template-columns: 124px 280px 135px;
                grid-template-rows: repeat(4, var(--pp-voyage-cell-height)) auto;
            }

            .underKeel .legend {
                justify-content: flex-start;
            }

            .underKeelNotes {
                grid-column: span 3;
            }

            .safetyHeaderPart2 {
                font-weight: normal;
                margin-left: 10px;
            }

            .standaloneTextArea {
                height: 126px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
            }

            .inputAndLabel {
                display: flex;
                flex-direction: column;
                flex-grow: 1;
            }

            .inputAndLabel:first-child {
                margin-right: 20px;
            }

            .inputAndLabel:last-child {
                margin-left: 20px;
            }

            .swDensity {
                width: 55px;
            }

            .bridgeTeamHeader {
                margin-top: 20px;
                margin-bottom: 15px;
                font-size: 14px;
            }

            .bridgeTeam {
                display: flex;
                flex-direction: row;
                margin-bottom: 20px;
            }

            voyage-checkbox {
                margin-right: 20px;
            }

            .pilotpoint-checkbox {
                margin-right: 0;
            }

            input:disabled, textarea:disabled, select:disabled, .container[read-only] .inputCell {
                background-color: var(--neutrals-grey-08, grey);
            }

            .signatures {
                display: grid;
                gap: 1px;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                width: 540px;
                grid-template-columns: 1fr 1fr;
                grid-template-rows: repeat(7, 51px);
            }
            .turn-units{
                padding: 0 5px 0 0;
            }

        `];
    }

    render()
    {
        if (!this._plan || !this._vesselCharacteristics)
        {
            return html``;
        }

        const vc = this._vesselCharacteristics;
        let calculatedValues: CalculatedValues|null = null;

        const currentSelectedRoute = this._plan.routes.find((route) => route.routeId === this._plan!.currentSelectedRouteId);
        const shouldGenerateCalculatedValues = isVesselOnCloud && !isOnline() && currentSelectedRoute?.recalcNeeded;
        
        // calculatedValues = DataExtractor.getCalculatedValues(this._plan, this._vesselCharacteristics, this.isOfflinePlan,this.showPassagePlanFromPlan)
        calculatedValues = shouldGenerateCalculatedValues ? DataExtractor.addOfflineCalculatedValues(this._plan, this._vesselCharacteristics) : DataExtractor.getCalculatedValues(this._plan, this._vesselCharacteristics, this.isOfflinePlan, this.showPassagePlanFromPlan);
        this._waypoints = DataExtractor.populateWaypoints(this._plan, vc.ecdisWPStartIndex, this.isOfflinePlan, this.showPassagePlanFromPlan);

        let minSpeedOpenWaters = this._plan.passagePlan.squats.minSpeed.openWaters;
        let maxSpeedOpenWaters = this._plan.passagePlan.squats.maxSpeed.openWaters;
        let bufferSpeedOpenWaters = this._plan.passagePlan.squats.bufferSpeed.openWaters;
        let vesselMaxSpeedOpenWaters = this._plan.passagePlan.squats.vesselMaxSpeed.openWaters;

        let minSpeedConfinedWaters = this._plan.passagePlan.squats.minSpeed.confinedWaters;
        let maxSpeedConfinedWaters = this._plan.passagePlan.squats.maxSpeed.confinedWaters;
        let bufferSpeedConfinedWaters = this._plan.passagePlan.squats.bufferSpeed.confinedWaters;
        let vesselMaxSpeedConfinedWaters = this._plan.passagePlan.squats.vesselMaxSpeed.confinedWaters;

        // const currentSelectedRouteIndex = this._plan.routes.findIndex(r => r.routeId === this._plan?.currentSelectedRouteId);
        const currentSelectedRouteIndex = this.showPassagePlanFromPlan ? this.plan!.selectedRouteIndex :  this.plan!.routes.findIndex((route) => route.routeId === this.plan!.currentSelectedRouteId);
        
        const berthToPilotDistance = calculatedValues?.berthToPilot !== null ? `${calculatedValues?.berthToPilot.toFixed(1)} nm` : "0 nm";
        const pilotToPilotDistance = calculatedValues?.pilotToPilot !== null ? `${calculatedValues?.pilotToPilot.toFixed(1)} nm` : "0 nm";
        const pilotToBerthDistance = calculatedValues?.pilotToBerth !== null ? `${calculatedValues?.pilotToBerth.toFixed(1)} nm` : "0 nm";
        const totalDistance = calculatedValues?.totalDistance !== null ? `${calculatedValues?.totalDistance.toFixed(1)} nm` : "0 nm";
        const berthToPilotSpeed = calculatedValues?.speedBerthToPilot !== null ? `${calculatedValues?.speedBerthToPilot.toFixed(1)} kt` : "0 kt";
        const pilotToPilotSpeed = calculatedValues?.speedPilotToPilot !== null ? `${calculatedValues?.speedPilotToPilot.toFixed(1)} kt` : "0 kt";
        const pilotToBerthSpeed = calculatedValues?.speedPilotToBerth !== null ? `${calculatedValues?.speedPilotToBerth.toFixed(1)} kt` : "0 kt";
        const totalSpeed = calculatedValues?.speedTotal !== null ? `${calculatedValues?.speedTotal.toFixed(1)} kt` : "0 kt";
        const berthToPilotTime = calculatedValues?.timeBerthToPilot !== null ? this._formatTime(calculatedValues?.timeBerthToPilot ?? 0) : "0";
        const pilotToPilotTime = calculatedValues?.timePilotToPilot !== null ? this._formatTime(calculatedValues?.timePilotToPilot ?? 0) : "0";
        const pilotToBerthTime = calculatedValues?.timePilotToBerth !== null ? this._formatTime(calculatedValues?.timePilotToBerth ?? 0) : "0";
        const totalTime = calculatedValues?.timeTotal !== null ? this._formatTime(calculatedValues?.timeTotal ?? 0) : "0";
      
        return html`
            <div class="container" ?read-only=${this.readOnly}>
                ${ this._waypoints.berthToPilot.length === 0 ? html``: html`
                <div class="waypointsHeader">
                    <h2>${this._plan ? this._plan.departureTerminalCode : ""} - OUT</h2>
                    <div class="waypointsHeaderRight">${this._plan ? TerminalsModel!.getCity(this._plan.departureTerminalCode) || this._plan.departureTerminalCode : ""} Out</div>
                </div>
                ${this._renderWaypoints(this._waypoints.berthToPilot, "Berth to Pilot", WPSections.BerthToPilot, vc)}
                `}

                ${ this._waypoints.pilotToPilot.length === 0 ? html``: html`
                <div class="waypointsHeader">
                    <h2>${this._plan ? this._plan.departureTerminalCode + " - " + this._plan.arrivalTerminalCode : ""}</h2>
                    <div class="waypointsHeaderRight">${this._plan ? TerminalsModel!.getCity((this._plan.departureTerminalCode) || this._plan.departureTerminalCode)  + " > "
                                                        + (TerminalsModel!.getCity(this._plan.arrivalTerminalCode) || this._plan.arrivalTerminalCode) : ""}</div>
                </div>
                ${this._renderWaypoints(this._waypoints.pilotToPilot, "Pilot to Pilot", WPSections.PilotToPilot, vc)}
                `}

                ${ this._waypoints.pilotToBerth.length === 0 ? html``: html`
                <div class="waypointsHeader">
                    <h2>${this._plan ? this._plan.arrivalTerminalCode : ""} - IN</h2>
                    <div class="waypointsHeaderRight">${this._plan ? TerminalsModel!.getCity(this._plan.arrivalTerminalCode) || this._plan.arrivalTerminalCode : ""} In</div>
                </div>
                ${this._renderWaypoints(this._waypoints.pilotToBerth, "Pilot to Berth", WPSections.PilotToBerth, vc)}
                `}

                ${ this._waypoints.berthToBerth.length === 0 ? html``: html`
                <div class="waypointsHeader">
                    <h2>${this._plan ? this._plan.arrivalTerminalCode : ""} - IN</h2>
                    <div class="waypointsHeaderRight">${this._plan ? TerminalsModel!.getCity(this._plan.arrivalTerminalCode) || this._plan.arrivalTerminalCode : ""} In</div>
                </div>
                ${this._renderWaypoints(this._waypoints.berthToBerth, "Berth to Berth", WPSections.BerthToBerth, vc)}
                `}

                <div class="flexRow">
                    <div>
                        <h2 class="tableTitle">DISTANCES</h2>
                        <div class="table distances" data-test="tbl-distances">
                            <div></div>
                            <div class="cell legendTop">Distances</div>
                            <div class="cell legendTop">Speed</div>
                            <div class="cell legendTop">Time</div>

                            <div class="cell legend">Berth to pilot (nm):</div>
                            <div class="cell" data-test="tbl-distance-berthToPilot">${berthToPilotDistance}</div>
                            <div class="cell" data-test="tbl-speed-berthToPilot">${berthToPilotSpeed}</div>
                            <div class="cell" data-test="tbl-time-berthToPilot">${berthToPilotTime}</div>

                            <div class="cell legend">Pilot to pilot (nm):</div>
                            <div class="cell" data-test="tbl-distance-pilotToPilot">${pilotToPilotDistance}</div>
                            <div class="cell" data-test="tbl-speed-pilotToPilot">${pilotToPilotSpeed}</div>
                            <div class="cell" data-test="tbl-time-pilotToPilot">${pilotToPilotTime}</div>

                            <div class="cell legend">Pilot to berth (nm):</div>
                            <div class="cell" data-test="tbl-distance-pilotToBerth">${pilotToBerthDistance}</div>
                            <div class="cell" data-test="tbl-speed-pilotToBerth">${pilotToBerthSpeed}</div>
                            <div class="cell" data-test="tbl-time-pilotToBerth">${pilotToBerthTime}</div>

                            <div class="cell legend">Total distance (nm):</div>
                            <div class="cell" data-test="tbl-totalDistance">${totalDistance}</div>
                            <div class="cell" data-test="tbl-totalSpeed">${totalSpeed}</div>
                            <div class="cell" data-test="tbl-totalTime">${totalTime}</div>
                        </div>
                    </div>

                    <div>
                        <h2 class="tableTitle">PLANNED DRAUGHTS ON PASSAGE</h2>
                        <div class="table draughts">
                            <div></div>
                            <div class="cell legendTop">Forward</div>
                            <div class="cell legendTop">Aft</div>
                            <div class="cell legendTop">Airdraft</div>
                            <div class="cell legendTop">SW Density</div>

                            <div class="cell legend">Berth to pilot</div>
                            <div class="cell inputCell">
                                <input id="drafts-berthToPilot-foreDraft" class="inlineInput" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.berthToPilot.foreDraft || this._plan.foreDraft}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.berthToPilot.foreDraft = <number>this._getValueFromInput(evt.target!)}> m
                            </div>
                            <div class="cell inputCell">
                                <input id="drafts-berthToPilot-aftDraft" class="inlineInput" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.berthToPilot.aftDraft || this._plan.aftDraft}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.berthToPilot.aftDraft = <number>this._getValueFromInput(evt.target!)}> m
                            </div>
                            <div class="cell inputCell">
                                <div id="drafts-berthToPilot-airDraft">${DataExtractor.getAirDraft(this._plan, vc,"berthToPilot" )} m</div>
                            </div>
                            <div class="cell inputCell">
                                <input id="drafts-berthToPilot-swDensity" class="inlineInput swDensity" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.berthToPilot.swDensity || ""}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.berthToPilot.swDensity = <number>this._getValueFromInput(evt.target!)}> g/cm^3
                            </div>

                            <div class="cell legend">Pilot to pilot</div>
                            <div class="cell inputCell">
                                <input id="drafts-pilotToPilot-foreDraft" class="inlineInput" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.pilotToPilot.foreDraft || this._plan.foreDraft}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.pilotToPilot.foreDraft = <number>this._getValueFromInput(evt.target!)}> m
                            </div>
                            <div class="cell inputCell">
                                <input id="drafts-pilotToPilot-aftDraft" class="inlineInput" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.pilotToPilot.aftDraft || this._plan.aftDraft}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.pilotToPilot.aftDraft = <number>this._getValueFromInput(evt.target!)}> m
                            </div>
                            <div class="cell inputCell">
                                <div id="drafts-pilotToPilot-airDraft">${DataExtractor.getAirDraft( this._plan, vc, "pilotToPilot")} m</div>
                            </div>
                            <div class="cell inputCell">
                                <input id="drafts-pilotToPilot-swDensity" class="inlineInput swDensity" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.pilotToPilot.swDensity || ""}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.pilotToPilot.swDensity = <number>this._getValueFromInput(evt.target!)}> g/cm^3
                            </div>

                            <div class="cell legend">Pilot to berth</div>
                            <div class="cell inputCell">
                                <input id="drafts-pilotToBerth-foreDraft" class="inlineInput" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.pilotToBerth.foreDraft || this._plan.foreDraft}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.pilotToBerth.foreDraft = <number>this._getValueFromInput(evt.target!)}> m
                            </div>
                            <div class="cell inputCell">
                                <input id="drafts-pilotToBerth-aftDraft" class="inlineInput" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.pilotToBerth.aftDraft || this._plan.aftDraft}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.pilotToBerth.aftDraft = <number>this._getValueFromInput(evt.target!)}> m
                            </div>
                            <div class="cell inputCell">
                                <div id="drafts-pilotToBerth-airDraft">${DataExtractor.getAirDraft(this._plan, vc, "pilotToBerth")} m</div>
                            </div>
                            <div class="cell inputCell">
                                <input id="drafts-pilotToBerth-swDensity" class="inlineInput swDensity" type="number" min="0" step="1"
                                       ?disabled=${this.readOnly}
                                       .value=${this._plan.passagePlan.drafts.pilotToBerth.swDensity || ""}
                                       @input=${(evt:InputEvent) => this._plan!.passagePlan.drafts.pilotToBerth.swDensity = <number>this._getValueFromInput(evt.target!)}> g/cm^3
                            </div>

                            <div></div>
                            <div class="draughtsNote">Note: All under keel clearances calculated on basis of above draughts</div>
                        </div>
                    </div>
                </div>

                <div class="flexRow">
                    <div>
                        <h2 class="tableTitle">CALCULATED SQUATS (SEE ALSO POSTER ON BRIDGE)</h2>
                        <div class="table squats">
                            <div></div>
                            <div class="cell legendTop">Speed</div>
                            <div class="cell legendTop">Open Waters</div>
                            <div class="cell legendTop">Confined Waters</div>

                            <div class="cell legend">Min. Speed</div>
                            <div class="cell" data-test="minSpeed-routeSpeed">${calculatedValues?.routeMinSpeed ? calculatedValues.routeMinSpeed!.toFixed(1) + " kt" : ""}</div>
                            <div class="cell" data-test="minSpeed-openWaters">${minSpeedOpenWaters ? minSpeedOpenWaters.toFixed(2)+" m" : ""}</div>
                            <div class="cell" data-test="minSpeed-confinedWaters">${minSpeedConfinedWaters ? minSpeedConfinedWaters.toFixed(2)+" m" : ""}</div>

                            <div class="cell legend">Max. Speed</div>
                            <div class="cell" data-test="maxSpeed-routeSpeed">${calculatedValues?.routeMaxSpeed ? calculatedValues.routeMaxSpeed!.toFixed(1) + " kt" : ""}</div>
                            <div class="cell" data-test="maxSpeed-openWaters">${maxSpeedOpenWaters ? maxSpeedOpenWaters.toFixed(2)+" m" : ""}</div>
                            <div class="cell" data-test="maxSpeed-confinedWaters">${maxSpeedConfinedWaters ? maxSpeedConfinedWaters.toFixed(2)+" m" : ""}</div>

                            <div class="cell legend">Buffer Speed</div>
                            <div class="cell" data-test="bufferSpeed-routeSpeed">${calculatedValues?.routeAverageSpeed ? calculatedValues.routeAverageSpeed!.toFixed(1) + " kt" : ""}</div>
                            <div class="cell" data-test="bufferSpeed-openWaters">${bufferSpeedOpenWaters ? bufferSpeedOpenWaters.toFixed(2)+" m" : ""}</div>
                            <div class="cell" data-test="bufferSpeed-confinedWaters">${bufferSpeedConfinedWaters ? bufferSpeedConfinedWaters.toFixed(2)+" m" : ""}</div>

                            <div class="cell legend">Vessel Max. Speed</div>
                            <div class="cell" data-test="vesselMaxSpeed-routeSpeed">${calculatedValues?.vesselMaxSpeed ? calculatedValues.vesselMaxSpeed.toFixed(1) + " kt" : ""}</div>
                            <div class="cell" data-test="vesselMaxSpeed-openWaters">${vesselMaxSpeedOpenWaters ? vesselMaxSpeedOpenWaters.toFixed(2)+" m" : ""}</div>
                            <div class="cell" data-test="vesselMaxSpeed-confinedWaters">${vesselMaxSpeedConfinedWaters ? vesselMaxSpeedConfinedWaters.toFixed(2)+" m" : ""}</div>
                        </div>
                    </div>

                    <div>
                        <h2 class="tableTitle">CALCULATED MINIMUM UNDER KEEL CLEARANCE</h2>
                        <div class="table underKeel">
                            <div></div>
                            <div class="cell legendTop">Required UKC</div>
                            <div class="cell legendTop">Minimum expected UKC</div>

                            <div class="cell legend">Alongside Departure*</div>
                            <textarea id="underKeelClearance-alongsideDeparture-required" class="cell" ?disabled=${this.readOnly}
                                      .value=${DataExtractor.getRequiredUKC("alongsideDeparture", this._plan)}
                                      @input=${(evt:InputEvent) => this._plan!.passagePlan.underKeelClearance.alongsideDeparture.required = <string>this._getValueFromInput(evt.target!)}>
                            </textarea>
                            <div class="cell">
                                ${this._plan.passagePlan.underKeelClearance.alongsideDeparture.minimumExpected !== null ? this._plan.passagePlan.underKeelClearance.alongsideDeparture.minimumExpected.toFixed(1) + " m" : ""}
                            </div>
                            <div class="cell legend">Alongside Arrival**</div>
                            <textarea id="underKeelClearance-alongsideArrival-required" class="cell" ?disabled=${this.readOnly}
                                      .value=${DataExtractor.getRequiredUKC("alongsideArrival", this._plan)}
                                      @input=${(evt:InputEvent) => this._plan!.passagePlan.underKeelClearance.alongsideArrival.required = <string>this._getValueFromInput(evt.target!)}>
                            </textarea>
                            <div class="cell">
                                    ${this._plan.passagePlan.underKeelClearance.alongsideArrival.minimumExpected !== null ? this._plan.passagePlan.underKeelClearance.alongsideArrival.minimumExpected.toFixed(1) + " m" : ""}
                            </div>
                            <div class="cell legend">Confined Water</div>
                            <textarea id="underKeelClearance-confinedWater-required" class="cell" ?disabled=${this.readOnly}
                                      .value=${DataExtractor.getRequiredUKC("confinedWater", this._plan)}
                                      @input=${(evt:InputEvent) => this._plan!.passagePlan.underKeelClearance.confinedWater.required = <string>this._getValueFromInput(evt.target!)}>
                            </textarea>
                            <div class="cell">
                                ${this._plan.passagePlan.underKeelClearance.confinedWater.minimumExpected !== null ? this._plan.passagePlan.underKeelClearance.confinedWater.minimumExpected.toFixed(1) + " m" : ""}
                            </div>

                            <div class="cell legend">Open Water</div>
                            <textarea id="underKeelClearance-openWater-required" class="cell" ?disabled=${this.readOnly}
                                      .value=${DataExtractor.getRequiredUKC("openWater", this._plan)}
                                      @input=${(evt:InputEvent) => this._plan!.passagePlan.underKeelClearance.openWater.required = <string>this._getValueFromInput(evt.target!)}>
                            </textarea>
                            <div class="cell">
                                ${this._plan.passagePlan.underKeelClearance.openWater.minimumExpected !== null ? this._plan.passagePlan.underKeelClearance.openWater.minimumExpected.toFixed(1) + " m" : ""}
                            </div>

                            <div class="underKeelNotes">
                                <div>* Alongside Departure UKC is calculated based on Planned Draught Berth to pilot</div>
                                <div>** Alongside Arrival UKC is calculated based on Planned Draught Pilot to berth</div>
                            </div>
                        </div>
                    </div>
                </div>

                <h2>SAFETY MESSAGES <span class="safetyHeaderPart2">EGC (SAT C) and NAVTEX messages to be received during entire voyage</span></h2>

                <div class="flexRow noMargin">
                    <div class="inputAndLabel">
                        <label for="safetyMessages-navareaRegions">NAVAREA region(s)</label>
                        <textarea id="safetyMessages-navareaRegions" class="standaloneTextArea" ?disabled=${this.readOnly}
                            .value=${DataExtractor.getUserOrSystemInputFromRoutePassagePlan(this._plan!.routes[currentSelectedRouteIndex], "safetyMessagesNavareaRegions").value}
                            @input=${(evt:InputEvent) => DataExtractor.setUserInputOnRoutePassagePlan(this._plan!.routes[currentSelectedRouteIndex], "safetyMessagesNavareaRegions", (<HTMLInputElement>evt.target)!.value)}></textarea>
                        </textarea>
                    </div>

                    <div class="inputAndLabel">
                        <label for="safetyMessages-navtexStations">NAVTEX stations</label>
                        <textarea id="safetyMessages-navtexStations" class="standaloneTextArea" ?disabled=${this.readOnly}
                            .value=${DataExtractor.getUserOrSystemInputFromRoutePassagePlan(this._plan!.routes[currentSelectedRouteIndex], "safetyMessagesNavtexStations").value}
                            @input=${(evt:InputEvent) => DataExtractor.setUserInputOnRoutePassagePlan(this._plan!.routes[currentSelectedRouteIndex], "safetyMessagesNavtexStations", (<HTMLInputElement>evt.target)!.value)}></textarea>
                        </textarea>
                    </div>
                </div>

                <h2>WEATHER INFORMATION</h2>

                <div class="flexRow noMargin">
                    <div class="inputAndLabel">
                        <label for="weatherInfo-navareaRegions">NAVAREA region(s)</label>
                        <textarea id="weatherInfo-navareaRegions" class="standaloneTextArea" ?disabled=${this.readOnly}
                            .value=${DataExtractor.getUserOrSystemInputFromRoutePassagePlan(this._plan!.routes[currentSelectedRouteIndex], "weatherInfoNavareaRegions").value || ""}
                            @input=${(evt:InputEvent) => DataExtractor.setUserInputOnRoutePassagePlan(this._plan!.routes[currentSelectedRouteIndex], "weatherInfoNavareaRegions", (<HTMLInputElement>evt.target)!.value)}>
                        </textarea>
                    </div>

                    <div class="inputAndLabel">
                        <label for="weatherInfo-navtexStations">NAVTEX stations</label>
                        <textarea id="weatherInfo-navtexStations" class="standaloneTextArea" ?disabled=${this.readOnly}
                            .value=${DataExtractor.getUserOrSystemInputFromRoutePassagePlan(this._plan!.routes[currentSelectedRouteIndex], "weatherInfoNavtexStations").value}
                            @input=${(evt:InputEvent) => DataExtractor.setUserInputOnRoutePassagePlan(this._plan!.routes[currentSelectedRouteIndex], "weatherInfoNavtexStations", (<HTMLInputElement>evt.target)!.value)}>
                        </textarea>
                    </div>

                    <div class="inputAndLabel">
                        <label for="weatherInfo-info">Weather information</label>
                        <textarea id="weatherInfo-info" class="standaloneTextArea" ?disabled=${this.readOnly}
                            .value=${this._plan.routes[currentSelectedRouteIndex].passagePlan!.weatherInfo.info}
                            @input=${(evt:InputEvent) => this._plan!.routes[currentSelectedRouteIndex].passagePlan!.weatherInfo.info = <string>this._getValueFromInput(evt.target!)}>
                        </textarea>
                    </div>
                </div>

                <h2>STATUS OF NAVIGATIONAL INSTRUMENTS / OTHER EQUIPMENT</h2>

                <div class="flexRow noMargin">
                    <div class="inputAndLabel">
                        <label for="equipmentStatus-operationalCondition">Operational condition</label>
                        <textarea id="equipmentStatus-operationalCondition" class="standaloneTextArea" ?disabled=${this.readOnly}
                                  .value=${this._plan!.routes[currentSelectedRouteIndex].passagePlan!.equipmentStatus.operationalCondition}
                                  @input=${(evt:InputEvent) => this._plan!.routes[currentSelectedRouteIndex].passagePlan!.equipmentStatus.operationalCondition = <string>this._getValueFromInput(evt.target!)}>
                        </textarea>
                    </div>

                    <div class="inputAndLabel">
                        <label for="equipmentStatus-failures">List of failures</label>
                        <textarea id="equipmentStatus-failures" class="standaloneTextArea" ?disabled=${this.readOnly}
                                  .value=${this._plan!.routes[currentSelectedRouteIndex].passagePlan!.equipmentStatus.failures}
                                  @input=${(evt:InputEvent) => this._plan!.routes[currentSelectedRouteIndex].passagePlan!.equipmentStatus.failures = <string>this._getValueFromInput(evt.target!)}>
                        </textarea>
                    </div>
                </div>

                <h2>MASTER'S ORDERS FOR THIS PASSAGE</h2>
                <textarea id="mastersOrders" class="standaloneTextArea" ?disabled=${this.readOnly}
                          .value=${this._plan!.routes[currentSelectedRouteIndex].passagePlan!.mastersOrders}
                          @input=${(evt:InputEvent) => {this._plan!.routes[currentSelectedRouteIndex].passagePlan!.mastersOrders = <string>this._getValueFromInput(evt.target!)}}>
                </textarea>

                <div class="bridgeTeamHeader">At ${this._plan ? TerminalsModel!.getCity(this._plan.departureTerminalCode) || this._plan.departureTerminalCode : ""} the Bridge Team will consist of:</div>
                <div class="bridgeTeam">
                    <voyage-checkbox id="bridgeTeam-departure-master" label="Master" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.departure.master}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.departure.master = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-departure-chiefOfficer" label="Chief Officer" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.departure.chiefOfficer}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.departure.chiefOfficer = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-departure-secondOfficer" label="Second Officer" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.departure.secondOfficer}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.departure.secondOfficer = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-departure-thirdOfficer" label="Third Officer" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.departure.thirdOfficer}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.departure.thirdOfficer = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-departure-pilot" label="Pilot" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.departure.pilot}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.departure.pilot = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-departure-helmsman" label="Helmsman" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.departure.helmsman}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.departure.helmsman = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-departure-lookout1" label="Lookout 1" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.departure.lookout1}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.departure.lookout1 = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-departure-lookout2" label="Lookout 2" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.departure.lookout2}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.departure.lookout2 = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                </div>

                <div class="bridgeTeamHeader">At ${this._plan ? TerminalsModel!.getCity(this._plan.arrivalTerminalCode) || this._plan.arrivalTerminalCode : ""} the Bridge Team will consist of:</div>
                <div class="bridgeTeam">
                    <voyage-checkbox id="bridgeTeam-arrival-master" label="Master" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.arrival.master}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.arrival.master = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-arrival-chiefOfficer" label="Chief Officer" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.arrival.chiefOfficer}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.arrival.chiefOfficer = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-arrival-secondOfficer" label="Second Officer" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.arrival.secondOfficer}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.arrival.secondOfficer = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-arrival-thirdOfficer" label="Third Officer" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.arrival.thirdOfficer}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.arrival.thirdOfficer = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-arrival-pilot" label="Pilot" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.arrival.pilot}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.arrival.pilot = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-arrival-helmsman" label="Helmsman" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.arrival.helmsman}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.arrival.helmsman = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-arrival-lookout1" label="Lookout 1" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.arrival.lookout1}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.arrival.lookout1 = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                    <voyage-checkbox id="bridgeTeam-arrival-lookout2" label="Lookout 2" ?disabled=${this.readOnly}
                                     .checked=${this._plan.passagePlan.bridgeTeam.arrival.lookout2}
                                     @input=${(evt:InputEvent) => this._plan!.passagePlan.bridgeTeam.arrival.lookout2 = (<VoyageCheckbox>evt.target).checked}>
                    </voyage-checkbox>
                </div>

                <h2 class="tableTitle">PLAN READ BY</h2>
                <div class="table signatures">
                    <div class="cell legendTop">Rank</div>
                    <div class="cell legendTop">Name</div>
                    ${this._plan.passagePlan.signatures.map( (signature: Signature, index) => html`
                            <div class="cell inputCell">
                                <select class="dropdown signature" ?disabled=${this.readOnly}
                                    .value=${signature.rank}
                                    @input=${(evt: InputEvent) => signature.rank = (<HTMLTextAreaElement>evt.target).value}>
                                    ${["", "Master", "Chief Officer", "Second Officer", "Third Officer", "Cadet"].map( (value) => html`
                                    <option value=${value} ?selected=${ value === signature.rank}>${value}</option>
                                    `)}
                                </select>
                            </div>
                            <div class="cell inputCell">
                                <input class="inlineInput signature" type="text" 
                                       ?disabled=${this.readOnly}
                                       .value=${signature.name}
                                       @input=${(evt:InputEvent) => signature.name = <string>this._getValueFromInput(evt.target!)}>
                            </div>
                        
                    `)}
                </div>
            </div>
        `;
    }

    @property({attribute: false}) private _waypoints: wpMetadata = {
        berthToPilot: [],
        pilotToPilot: [],
        pilotToBerth: [],
        berthToBerth: []
    };
    @property({attribute: false}) private _vesselCharacteristics: VesselCharacteristics|null = null;
    @property({attribute: false}) private _selectedTideItem?: number;
    private _plan: VoyagePlan|null = null;
    private _savingPlanCounter: number = 0;

    _renderWaypoints(waypoints: WPAndMetadata[], distanceText: string, wpSection: WPSections, vesselCharacteristics: VesselCharacteristics)
    {
        const totalDistance = (waypoints.reduce( (acc, wpInfo, i) => i !== waypoints.length-1 ? acc + wpInfo.metadata.distance.toNextWaypoint!: acc, 0))/1.852;

        const formatCourse = (course: number|null|undefined) => {
            if (course == null) {
                return "";
            }
            let courseParts = course.toFixed(1).split(".");
            return `${courseParts[0]}°${courseParts[1]}`;
        };

        const formatPosition = (number: number, type: string) => {
            const degrees = Math.floor(Math.abs(number));
            const minutes = (Math.abs(number) - degrees)*60;
            let hemisphere;
            if (type === "lat")
            {
                hemisphere = number >= 0 ? "N" : "S"
            }
            else
            {
                hemisphere = number >= 0 ? "E" : "W";
            }
            return `${degrees}°${minutes.toFixed(2)}${hemisphere}`;
        };

        const formatSquat = (squat: number|undefined, openWaters: boolean|undefined, idx: number, wpSection: WPSections, vesselCharacteristics: VesselCharacteristics, speed_maximum?: number) => {
            if (squat === undefined) {
                return html``;
            }
            
            if (openWaters === undefined) {
                const isLastSegmentWP = idx === waypoints.length - 1;
                const isPilotToPilot = wpSection === WPSections.PilotToPilot;
                const isBerthToBerth = wpSection === WPSections.BerthToBerth;
                const isLastBerthToPilotWP = isLastSegmentWP && wpSection === WPSections.BerthToPilot;
                
                // Default is confined waters for berth -> pilot and pilot -> berth, open waters for pilot -> pilot and berth -> berth
                if ((!isLastSegmentWP && (isPilotToPilot || isBerthToBerth)) || isLastBerthToPilotWP)
                {
                    openWaters = true;
                }
                else
                {
                    openWaters = false;
                }
            }

            let legSpeedSetting = ""
            if (vesselCharacteristics) {
                if( vesselCharacteristics.legSpeedSetting === LegSpeedSettings.MAX_SPEED && speed_maximum ){
                    legSpeedSetting = "M"
                } else {
                    legSpeedSetting = "P"
                }
            }

            return html`<span class="openWaters">${openWaters ? "O" : "C"}${legSpeedSetting}</span> ${squat.toFixed(2)}`;
        }

        return html`
        <div class="table waypoints ${wpSection === WPSections.BerthToPilot || wpSection === WPSections.PilotToBerth ? "nocatzoc" : ""}">
            <div class="cell legendTop doubleRow">WP #</div>
            <div class="cell legendTop doubleRow">Pilot WP</div>
            <div class="cell legendTop doubleColumn">Waypoint Position</div>
            <div class="cell legendTop">RL</div>
            <div class="cell legendTop doubleRow">Course</div>
            <div class="cell legendTop doubleRow">Turnrate (°/min)</div>
            <div class="cell legendTop doubleRow">Turn Circle (nm)</div>
            <div class="cell legendTop doubleColumn">Distance</div>
            <div class="cell legendTop doubleColumn">Speed (kt)</div>
            <div class="cell legendTop doubleRow wpDetails">Waypoint Details</div>
            <div class="cell legendTop passagePlan">Passage Plan</div>
            <div class="cell legendTop">Latitude</div>
            <div class="cell legendTop">Longitude</div>
            <div class="cell legendTop">GC</div>
            <div class="cell legendTop">To Waypoint</div>
            <div class="cell legendTop">Remaining</div>
            <div class="cell legendTop">Min</div>
            <div class="cell legendTop">Max.</div>
            ${wpSection === WPSections.PilotToPilot || wpSection === WPSections.BerthToBerth ? html`
            <div class="cell legendTop">CatZoc</div>
            `:html``}
            <div class="cell legendTop lop">Position cross check interval</div>
            <div class="cell legendTop">Max. X track (nm)</div>
            <div class="cell legendTop">Min. depth (m)</div>
            <div class="cell legendTop">Tide (m)</div>
            <div class="cell legendTop">Squat* (m)</div>
            <div class="cell legendTop">UKC</div>

        
            ${waypoints.map( (waypoint, index) => html`
                <div class="cell" data-test='wp-num-${waypoint.metadata.sequenceNumber}'>${waypoint.metadata.sequenceNumber}</div>
                <div class="cell">
                    <voyage-checkbox class="pilotpoint-checkbox" id="pilotpoint-checkbox" data-test=${`wp-pilotPoint-${waypoint.metadata.sequenceNumber}`} data-id=${`wp-${waypoint.metadata.sequenceNumber}-pilotPoint`}
                           .checked=${!!waypoint.waypoint.properties.pilotPoint}
                           .disabled=${this.readOnly}
                           @input=${(evt:InputEvent) => waypoint.waypoint.properties.pilotPoint = (<HTMLInputElement>evt.target).checked}></voyage-checkbox>
                </div>
                <div class="cell" data-test='wp-lat-num-${waypoint.metadata.sequenceNumber}'>${formatPosition(waypoint.coordinates[1], "lat")}</div>
                <div class="cell" data-test='wp-lon-num-${waypoint.metadata.sequenceNumber}'>${formatPosition(waypoint.coordinates[0], "lon")}</div>
                <div class="cell" data-test='wp-lt-num-${waypoint.metadata.sequenceNumber}'>${waypoint.metadata.legType}</div>
                <div class="cell" data-test='wp-course-num-${waypoint.metadata.sequenceNumber}'>${formatCourse(waypoint.metadata.course)}</div>
                <div class="cell inputCell noPadding">
                    <input data-test='wp-turnrate-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-turnrate`} ?disabled=${this.readOnly}
                        .value=${until(DataExtractor.computeRateOfTurn(waypoint.waypoint, "turn_radius", waypoints, this._waypoints, index) || "")}
                        class="inlineInput" type="number" min="0" step="0.1" 
                        @input=${(evt: InputEvent) => DataExtractor.setUserInputOnWP(waypoint.waypoint, "rate_of_turn", Number((<HTMLInputElement>evt.target).value)) }
                    >
                      <span class="turn-units">${until(DataExtractor.computeRateOfTurn(waypoint.waypoint, "turn_radius", waypoints, this._waypoints, index).then(value =>
                            value ? '°/min' : ''
                        ), '')}</span>
                </div>
                <div class="cell inputCell noPadding">
                    <input  data-test='wp-turnradius-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-turnradius`} ?disabled=${this.readOnly}
                        .value=${`${DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "turn_radius") ?? DataExtractor.computeTurnRadius(waypoint.waypoint, "turn_radius", waypoints, this._waypoints, index) ?? ""}`}
                        class="inlineInput" type="number" min="0" step="0.1" 
                        @input=${async (evt: InputEvent) => {
                            const raw = (<HTMLInputElement>evt.target).value;
                            DataExtractor.setUserInputOnWP(waypoint.waypoint, "turn_radius", raw ? Number(raw) : undefined);
                            await DataExtractor.computeRateOfTurn(waypoint.waypoint, "turn_radius", waypoints, this._waypoints, index);
                            this.requestUpdate();
                        } }
                    >
                    <span class="turn-units">${`${(DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "turn_radius") ?? DataExtractor.computeTurnRadius(waypoint.waypoint, "turn_radius", waypoints, this._waypoints, index)) ? 'nm' : '' }`}</span>
                </div>
                <div class="cell" data-test='wp-distance-to-num-${waypoint.metadata.sequenceNumber}'>${(waypoint.metadata.distance.toNextWaypoint!/1.852).toFixed(1)} nm</div>
                <div class="cell" data-test='wp-distance-remaining-num-${waypoint.metadata.sequenceNumber}'>${(waypoint.metadata.distance.remaining/1.852).toFixed(1)} nm</div>
                <div class="cell" data-test='wp-speed-min-num-${waypoint.metadata.sequenceNumber}'>${waypoint.waypoint.properties.speed_minimum != undefined ? (waypoint.waypoint.properties.speed_minimum*MpsToKts).toFixed(1) : ""}</div>
                <div class="cell" data-test='wp-speed-max-num-${waypoint.metadata.sequenceNumber}'>${waypoint.waypoint.properties.speed_maximum != undefined ? (waypoint.waypoint.properties.speed_maximum*MpsToKts).toFixed(1) : ""}</div>
                <textarea data-test='wp-name-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-name`} ?disabled=${this.readOnly}
                          .value=${waypoint.waypoint.properties.name || ""} class="cell"
                        @input=${(evt: InputEvent) => waypoint.waypoint.properties.name = (<HTMLTextAreaElement>evt.target).value.toString()}></textarea>
                ${wpSection === WPSections.PilotToPilot || wpSection === WPSections.BerthToBerth ? html`
                <div class="cell">
                    <select class="dropdown" data-test='wp-catzoc-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-catzoc`} ?disabled=${this.readOnly}
                        .value=${DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "catzoc") || ""}
                        @input=${(evt: InputEvent) => DataExtractor.setUserInputOnWP(waypoint.waypoint, "catzoc", (<HTMLTextAreaElement>evt.target).value.toString())}>
                        ${["", "A1", "A2", "B", "C", "D", "U"].map( (value) => html`
                        <option value=${value} ?selected=${ value === DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "catzoc")}>${value}</option>
                        `)}
                    </select>
                </div>
                ` : html``}
                <textarea data-test='wp-interval-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-lop`} ?disabled=${this.readOnly}
                          .value=${waypoint.waypoint.properties.lop || ""} class="cell"
                        @input=${(evt: InputEvent) => waypoint.waypoint.properties.lop = (<HTMLTextAreaElement>evt.target).value.toString()}></textarea>
                <div class="cell inputCell noPadding">
                    <input data-test='wp-max-x-track-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-max_x_track`} 
                            .value=${DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "max_x_track") || 0} 
                            class="inlineInput" type="number" min="0" step="0.01" ?disabled=${this.readOnly}
                            @input=${async (evt: InputEvent) => { await DataExtractor.updateMaxXTrack(waypoint.waypoint, "max_x_track", waypoints, this._waypoints, index, (<HTMLInputElement>evt.target).value ? Number((<HTMLInputElement>evt.target).value) : undefined); this.requestUpdate(); }}>
                </div>
                <div class="cell inputCell noPadding">
                    <input data-test='wp-mindepth-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-min_depth`} ?disabled=${this.readOnly}
                           .value=${(() => {
                                    const minDepthVal = DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "min_depth");
                                    return (minDepthVal !== undefined && minDepthVal !== null)
                                        ? Number(minDepthVal).toFixed(1)
                                        : "0";
                                    })()}
                           class="inlineInput" type="number" min="0" step="0.1" 
                           @input=${(evt: InputEvent) => DataExtractor.setUserInputOnWP(waypoint.waypoint, "min_depth", (<HTMLInputElement>evt.target).value ? Number((<HTMLInputElement>evt.target).value) : undefined) }>
                </div>
                <div class="cell inputCell noPadding">
                    ${this._tideInput(waypoint, wpSection, index)}  
                </div>
                <div class="cell" data-test='wp-squat-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-squat`}>
                    ${formatSquat(waypoint.waypoint.properties.squat, waypoint.waypoint.properties.openWaters, index, wpSection, vesselCharacteristics, waypoint.waypoint.properties.speed_maximum)}
                </div>
                <div class="cell" data-test='wp-ukc-num-${waypoint.metadata.sequenceNumber}' data-id=${`wp-${waypoint.metadata.sequenceNumber}-ukc`}>
                    ${waypoint.waypoint.properties.ukc != undefined ? waypoint.waypoint.properties.ukc.toFixed(1) + " m" : ""}
                </div>
            `)}
        </div>
        <div class="waypointsNotes">* C - Confined Waters &emsp; O - Open Waters  &emsp; M - Squat based on max leg speed  &emsp; P - Squat based on calculated planned leg speed'</div>
        <div class="waypointsDistance">${distanceText} Distance: ${totalDistance.toFixed(1)} nm</div>

        <div class="warning">
            Have in mind, that positions of buoys might differ between the chart and the actual positions.<br>
            Buoys may also be extinguised or missing, ask the pilot if there is any conditions we should know about.<br>
            Do not rely on one position reference only, especially not on buoys<br>
        </div>
        `;
    }

    _tideInput(waypoint: WPAndMetadata, wpSection: WPSections, selectedTideIndex:number):TemplateResult
    {
        
        const tide = DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "tide");
        const { sequenceNumber } = waypoint.metadata;
        
        if ((wpSection === WPSections.PilotToPilot || wpSection === WPSections.BerthToBerth) && this._selectedTideItem !== selectedTideIndex && tide === 0) {
            return html`<div @click=${() =>{
                this._selectedTideItem = selectedTideIndex;
            }}>N/A</div>`
        }
        
        return html`
           <input data-test='wp-tide-num-${sequenceNumber}' data-id=${`wp-${sequenceNumber}-tide`}
                .value=${`${tide}` ?? "" }
                class="inlineInput" type=${"number"} step="0.01" ?disabled=${this.readOnly}
                @input=${(evt: InputEvent) => {
                    DataExtractor.setUserInputOnWP(waypoint.waypoint, "tide", Number((<HTMLInputElement>evt.target).value) ?? null);
                }}>
        `
    }

    _formatTime(time: number) {
        return `${Math.floor(time)} h ${Math.floor((time - Math.floor(time))*60)} min`;
    }

    _getValueFromInput(inputElement: EventTarget)
    {
        const input = <VoyageCheckbox|HTMLInputElement|HTMLTextAreaElement>inputElement;

        if (input instanceof VoyageCheckbox || input.type === "checkbox")
        {
            return (<VoyageCheckbox|HTMLInputElement>input).checked;
        }
        else if (input.type === "number")
        {
            return input.value ? Number(input.value) : null;
        }
        else
        {
            // Assume text field
            return input.value;
        }
    }

    _onInput(evt: InputEvent)
    {
        const target = <HTMLInputElement|VoyageCheckbox>evt.target;
        if (target instanceof HTMLInputElement && target.type === "checkbox")
        {
            if (!this.isOfflinePlan) {
                // Don't wait for debounce for pilot point checkboxes
                VesselPlanningModel!.setPlan(this._plan!.scheduleId, this._plan!, false);
            }

            // Fix up for caching issue
            target.checked = !target.checked;
            target.blur();
            return;
        }
        
        const shouldCalcUKC = (target.id && target.id.includes("drafts"))
                                || (target.getAttribute("data-id") && (
                                    target.getAttribute("data-id")!.includes("min_depth")
                                    || target.getAttribute("data-id")!.includes("tide")
                                    || target.getAttribute("data-id")!.includes("catzoc")
                                ))

        if (shouldCalcUKC) {
            this._calcUkc()
        }
        VesselPlanningModel!._localPassageUpdated();
    }

    _mergeSavedPlan(currentPlan: VoyagePlan, savedPlan: VoyagePlan)
    {
        // The current plan should be kept in preference to the saved plan,
        // except for any calculated values
        const newPlan: VoyagePlan = JSON.parse(JSON.stringify(currentPlan));
        
        newPlan.passagePlan.calculatedValues = savedPlan.passagePlan.calculatedValues;

        newPlan.passagePlan.underKeelClearance.alongsideDeparture.minimumExpected = savedPlan.passagePlan.underKeelClearance.alongsideDeparture.minimumExpected;
        newPlan.passagePlan.underKeelClearance.alongsideArrival.minimumExpected = savedPlan.passagePlan.underKeelClearance.alongsideArrival.minimumExpected;
        newPlan.passagePlan.underKeelClearance.confinedWater.minimumExpected = savedPlan.passagePlan.underKeelClearance.confinedWater.minimumExpected;
        newPlan.passagePlan.underKeelClearance.openWater.minimumExpected = savedPlan.passagePlan.underKeelClearance.openWater.minimumExpected;

        for (let i = 0; i < currentPlan.routes[currentPlan.selectedRouteIndex].route_geometry.length; i++)
        {
            // The only waypoint property that is calculated when saving the plan is ukc
            const feature = newPlan.routes[newPlan.selectedRouteIndex].route_geometry[i];
            if (feature.geometry.type === "Point" && feature.properties.type === "Hard")
            {
                const savedFeature = savedPlan.routes[savedPlan.selectedRouteIndex].route_geometry[i];
               
            }
        }

        return newPlan;
    }

    async _getRateOfTurnNumber(waypoint: any): Promise<number | ""> {
        // get current rate from waypoint
        let rateOfTurn = DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "rate_of_turn");

        // calculate if rate of turn is missing
        if (!rateOfTurn) {
            rateOfTurn = await DataExtractor._calculateRateOfTurnForWaypoint(waypoint);
        }

        return rateOfTurn !== undefined && rateOfTurn !== null ? rateOfTurn : "";
    }
}
